import {isMegaPay} from '../../../../../../_metronic/helpers'
import {
  formatPersianDateTime,
  normalizeDigits,
  persianDateToTehranEpoch,
} from '../../../tax-workspace/core/tehranDate'
import {Invoice, InvoiceExchangeAnnouncementSummary} from './_models'

export type InvoiceStatusInfo = {
  code?: number
  label: string
  shortLabel?: string
  color: string
  icon: string
  raw?: string
  final?: boolean
}

const clean = (value?: string | number | null) => `${value ?? ''}`.trim()
const toPersianNumber = (value: number) =>
  new Intl.NumberFormat('fa-IR', {useGrouping: false}).format(value)

const getWaitingReactionLabel = (invoice: Invoice) => {
  if (!invoice.brw) return 'در انتظار واکنش'
  if (invoice.brw.od) return 'مهلت واکنش پایان یافته؛ در انتظار استعلام'
  return `در انتظار واکنش · ${toPersianNumber(invoice.brw.rd)} روز مانده`
}

export const isTaxGovApproved = (status?: number | null): boolean =>
  status !== undefined && status !== null && status >= 1 && status <= 5

// Phase three without an approved tax status means the send is proven by a
// reference number while the inquiry result has not arrived yet.
export const isAwaitingInquiryResult = (invoice: Invoice): boolean =>
  invoice.ph?.i === 3 && !isTaxGovApproved(invoice.tgs)

// The invoice left registration but has no definitive tax service result yet,
// so its send attempt is still worth showing under the row.
export const hasInFlightSendState = (invoice: Invoice): boolean =>
  invoice.ph?.i === 2 || isAwaitingInquiryResult(invoice)

// The organisation usually answers within minutes of a send, so a list that holds an invoice
// sent in the last hour and still waiting is polled every ten seconds; older ones wait for the
// user's own refresh.
export const SEND_STATE_AUTO_REFRESH_WINDOW_MS = 60 * 60 * 1000
export const SEND_STATE_AUTO_REFRESH_INTERVAL_MS = 10_000

// snd.dt is the send log's Tehran wall-clock time, "1405/06/26 08:49:18" (seconds optional).
export const getSentAtEpoch = (invoice: Invoice): number | undefined => {
  const match = /^(\d{4}\/\d{1,2}\/\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(
    normalizeDigits(clean(invoice.snd?.dt))
  )
  if (!match) return undefined
  try {
    const [, day, hour = '0', minute = '0', second = '0'] = match
    return (
      persianDateToTehranEpoch(day) +
      (Number(hour) * 3600 + Number(minute) * 60 + Number(second)) * 1000
    )
  } catch {
    return undefined
  }
}

// A client clock a few minutes behind the server must not hide a send made just now.
const CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000

export const isRecentlySentAwaitingResult = (invoice: Invoice, now = Date.now()): boolean => {
  if (!hasInFlightSendState(invoice) && !hasAmbiguousSendClaim(invoice)) return false
  const sentAt = getSentAtEpoch(invoice)
  if (sentAt === undefined) return false
  const age = now - sentAt
  return age > -CLOCK_SKEW_TOLERANCE_MS && age < SEND_STATE_AUTO_REFRESH_WINDOW_MS
}

// react-query refetchInterval for a list page: polls while any row on it is a fresh, waiting send.
export const getSendStateRefetchInterval = (
  invoices: Invoice[] | undefined,
  now = Date.now()
): number | false =>
  invoices?.some((invoice) => isRecentlySentAwaitingResult(invoice, now))
    ? SEND_STATE_AUTO_REFRESH_INTERVAL_MS
    : false

export const AMBIGUOUS_SEND_LABEL = 'ارسال شد؛ نتیجه نامشخص — در حال استعلام از سامانه'
// A lost answer the automatic inquiry does not cover (a sandbox-token claim, for example): only
// support can settle it, so the label must not say an inquiry is running.
export const AMBIGUOUS_SEND_REVIEW_LABEL = 'ارسال شد؛ نتیجه نامشخص — نیازمند بررسی پشتیبانی'

// History sendState of a send whose answer was lost: 'ambiguous' is settled by inquiry and
// 'ambiguous_review' waits for support. Both keep the invoice fenced.
export const isAmbiguousSendState = (sendState?: string): boolean =>
  sendState === 'ambiguous' || sendState === 'ambiguous_review'

// The invoice was sent but the tax system's answer was lost, whatever its phase. The result is
// settled by inquiry, so it must not be restored, reset or sent again meanwhile.
export const hasAmbiguousSendClaim = (invoice: Invoice): boolean => invoice.snd?.amb === true

// The server puts the right wording (inquiry running, or support review) in snd.st.
export const getAmbiguousSendLabel = (invoice: Invoice): string =>
  invoice.snd?.st?.trim() || AMBIGUOUS_SEND_LABEL

// Until the tax service approves the invoice, its tax ID is not final and may
// still change, so printed copies must mark it as provisional.
export const isTaxIdFinal = (invoice: Invoice): boolean =>
  invoice.ph?.i === 3 && isTaxGovApproved(invoice.tgs)

export const PROVISIONAL_TAX_ID_NOTICE =
  'این صورتحساب هنوز در سامانه مؤدیان تأیید نشده است؛ بنابراین شماره منحصر به فرد مالیاتی آن قطعی نیست و ممکن است تغییر کند.'

export const isSandboxInvoice = (invoice: Invoice): boolean => invoice.fme === 'Sandbox'

export const getTaxGovStatusInfo = (status?: number | null): InvoiceStatusInfo => {
  switch (status) {
    case 1:
      return {
        code: status,
        label: 'تأیید API',
        shortLabel: 'API',
        color: 'success',
        icon: 'la-check-double',
        final: true,
      }
    case 2:
      return {
        code: status,
        label: 'تأیید دستی',
        shortLabel: 'دستی',
        color: 'success',
        icon: 'la-hand-pointer',
        final: true,
      }
    case 3:
      return {
        code: status,
        label: 'تأیید فایل Excel',
        shortLabel: 'Excel',
        color: 'success',
        icon: 'la-file-excel',
        final: true,
      }
    case 4:
      return {
        code: status,
        label: 'تأیید فایل PDF',
        shortLabel: 'PDF',
        color: 'success',
        icon: 'la-file-pdf',
        final: true,
      }
    case 5:
      return {
        code: status,
        label: 'تأیید استعلام کارپوشه',
        shortLabel: 'استعلام',
        color: 'success',
        icon: 'la-search',
        final: true,
      }
    default:
      return {
        code: status ?? undefined,
        label: 'وضعیت تأیید نامشخص',
        shortLabel: 'نامشخص',
        color: 'warning',
        icon: 'la-question-circle',
        final: false,
      }
  }
}

export const isAccessDeniedInquiryError = (error?: string | null): boolean =>
  clean(error).toUpperCase().includes('ACCESS_DENIED')

export const getBuyerStatusInfo = (invoice: Invoice): InvoiceStatusInfo | undefined => {
  if ((invoice.ph?.i ?? 0) < 3) {
    return undefined
  }

  const raw = clean(invoice.bsr)
  const normalized = raw.toUpperCase()
  const isCanceled = normalized === 'CANCELED' || normalized === 'CANCELLED'
  const isSystemicRejected = normalized === 'SYSTEMIC_REJECTED'
  const code =
    invoice.bst === 0
      ? 100
      : invoice.bst ||
        (normalized === 'AWAITING_REACTION'
          ? 100
          : normalized === 'SYSTEMIC_APPROVED'
          ? 1
          : normalized === 'APPROVED'
          ? 2
          : normalized === 'REJECTED' || isSystemicRejected || isCanceled
          ? 3
          : normalized === 'NO_NEED_REACTION' || normalized === 'IMPOSSIBLE_REACTION'
          ? 4
          : undefined)

  switch (code ?? (invoice.brw ? 100 : undefined)) {
    case 100:
      return {
        code,
        label: getWaitingReactionLabel(invoice),
        shortLabel: invoice.brw?.od
          ? 'سررسیدشده'
          : invoice.brw
          ? `${toPersianNumber(invoice.brw.rd)} روز مانده`
          : 'انتظار',
        color: 'warning',
        icon: 'la-hourglass-half',
        raw,
        final: false,
      }
    case 1:
      return {
        code,
        label: 'تأیید سیستمی',
        shortLabel: 'سیستمی',
        color: 'success',
        icon: 'la-check',
        raw,
        final: true,
      }
    case 2:
      return {
        code,
        label: 'تأیید شده',
        shortLabel: 'تأیید',
        color: 'success',
        icon: 'la-check-double',
        raw,
        final: true,
      }
    case 3:
      return {
        code,
        label: isCanceled ? 'باطل شده' : isSystemicRejected ? 'رد سیستمی' : 'رد شده',
        shortLabel: isCanceled ? 'باطل' : isSystemicRejected ? 'رد سیستمی' : 'رد',
        color: 'danger',
        icon: isSystemicRejected ? 'la-ban' : 'la-times-circle',
        raw,
        final: true,
      }
    case 4:
      return {
        code,
        label: normalized === 'IMPOSSIBLE_REACTION' ? 'عدم امکان واکنش' : 'عدم نیاز به واکنش',
        shortLabel: normalized === 'IMPOSSIBLE_REACTION' ? 'غیرممکن' : 'بی‌نیاز',
        color: 'success',
        icon: 'la-check',
        raw,
        final: true,
      }
    default:
      return raw
        ? {
            label: `وضعیت کارپوشه: ${raw}`,
            shortLabel: 'کارپوشه',
            color: 'info',
            icon: 'la-user-check',
            raw,
            final: false,
          }
        : undefined
  }
}

export const getArticle6StatusInfo = (invoice: Invoice): InvoiceStatusInfo | undefined => {
  const raw = clean(invoice.a6s)
  if (!raw) {
    return undefined
  }

  const normalized = raw.toLowerCase()
  const notExceeded =
    raw.includes('عدم') ||
    ['0', 'false', 'no', 'not_exceeded', 'no_deviation', 'not_deviated', 'not_violated'].includes(
      normalized
    ) ||
    normalized.includes('not_exceed') ||
    normalized.includes('no_deviation') ||
    normalized.includes('not_viol')

  if (notExceeded) {
    return {
      label: 'عدم عدول',
      shortLabel: 'عدم عدول',
      color: 'success',
      icon: 'la-shield-alt',
      raw,
      final: true,
    }
  }

  const exceeded =
    raw.includes('عدول') ||
    ['1', 'true', 'yes', 'exceeded', 'deviation', 'deviated', 'violated', 'violation'].includes(
      normalized
    ) ||
    normalized.includes('exceed') ||
    normalized.includes('deviation') ||
    normalized.includes('violat')

  if (exceeded) {
    return {
      label: 'عدول',
      shortLabel: 'عدول',
      color: 'danger',
      icon: 'la-exclamation-triangle',
      raw,
      final: true,
    }
  }

  return {
    label: `ماده ۶: ${raw}`,
    shortLabel: 'ماده ۶',
    color: 'info',
    icon: 'la-balance-scale',
    raw,
    final: false,
  }
}

// Who recorded the pattern-11 exchange sale announcement status and when (badge tooltip).
export const getExchangeAnnouncementSourceText = (
  summary?: InvoiceExchangeAnnouncementSummary | null
): string => {
  if (!summary) return ''

  const when =
    clean(summary.setAt) || (clean(summary.setAtUtc) ? formatPersianDateTime(summary.setAtUtc) : '')
  const at = when ? ` در ${when}` : ''
  if (summary.source === 'orgApi') return `ثبت از سامانه${at}`
  if (summary.source === 'manual')
    return `ثبت دستی توسط ${clean(summary.setBy) || 'کاربر نامشخص'}${at}`
  return ''
}

export const getExchangeAnnouncementStatusInfo = (
  summary?: InvoiceExchangeAnnouncementSummary | null
): InvoiceStatusInfo | undefined => {
  switch (summary?.status) {
    case 'confirmed':
      return {
        label: 'اعلامیه بورس: تأیید شده',
        color: 'success',
        icon: 'la-stamp',
        raw: getExchangeAnnouncementSourceText(summary) || undefined,
        final: true,
      }
    case 'notConfirmed':
      return {
        label: 'اعلامیه بورس: تأیید نشده',
        color: 'warning',
        icon: 'la-hourglass-half',
        raw: getExchangeAnnouncementSourceText(summary) || undefined,
        final: false,
      }
    case 'notRecorded':
      return {
        label: 'اعلامیه بورس: ثبت نشده',
        color: 'secondary',
        icon: 'la-question-circle',
        raw: 'وضعیت تأیید اعلامیه فروش بورس هنوز ثبت نشده است؛ تا ثبت تأیید، صدور اصلاحی مجاز است.',
        final: false,
      }
    default:
      return undefined
  }
}

// Pattern 11 only: the server sends the summary just for these rows, so every other invoice keeps
// its badges unchanged.
export const getExchangeAnnouncementInfo = (invoice: Invoice): InvoiceStatusInfo | undefined =>
  invoice.tmpi === 11 ? getExchangeAnnouncementStatusInfo(invoice.exchangeAnnouncement) : undefined

export const canOpenBuyerStatusPanel = (invoice: Invoice): boolean => {
  return (
    isMegaPay &&
    invoice.act === true &&
    invoice.typi === 1 &&
    invoice.sbji !== 3 &&
    !!clean(invoice.txi) &&
    !!invoice.ph &&
    invoice.ph.i >= 3 &&
    isTaxGovApproved(invoice.tgs)
  )
}

export const needsBuyerStatusInquiry = (invoice: Invoice): boolean => {
  if (!canOpenBuyerStatusPanel(invoice)) {
    return false
  }

  const buyerStatus = getBuyerStatusInfo(invoice)
  const article6Status = getArticle6StatusInfo(invoice)

  return !buyerStatus || buyerStatus.code === 100 || !article6Status
}

export const getSendLogActionLabel = (action: number): string => {
  switch (action) {
    case 0:
      return 'ثبت صورتحساب'
    case 1:
      return 'ارسال'
    case 2:
      return 'استعلام نتیجه ارسال'
    case 3:
      return 'استعلام با UID'
    case 4:
      return 'استعلام وضعیت کارپوشه'
    case 5:
      return 'رویداد دریافت صورتحساب'
    case 6:
      return 'ثبت پرداخت در سامانه مؤدیان'
    default:
      return 'استعلام'
  }
}

// sendState and statusLabel are sent only by newer backends; without them the status code alone
// decides. On a lost answer the server's label wins, so it can say who settles it.
export const getSendLogStatusInfo = (
  status?: number | null,
  sendState?: string,
  statusLabel?: string
): InvoiceStatusInfo => {
  if (isAmbiguousSendState(sendState)) {
    const fallback =
      sendState === 'ambiguous_review' ? AMBIGUOUS_SEND_REVIEW_LABEL : AMBIGUOUS_SEND_LABEL
    return {label: statusLabel?.trim() || fallback, color: 'warning', icon: 'la-question-circle'}
  }

  switch (status) {
    case 1:
      return {label: 'موفق', color: 'success', icon: 'la-check-circle'}
    case 2:
      return {label: 'در حال انجام', color: 'warning', icon: 'la-hourglass-half'}
    // 3: the request went out and no answer came back (a payment registration whose
    // answer was lost) — neither a failure nor still running.
    case 3:
      return {label: 'نتیجه نامشخص', color: 'warning', icon: 'la-question-circle'}
    // 4: what a registration noted without stopping it (the buyer's tax file, a goods id taken
    // from the business's goods); backend 718.
    case 4:
      return {label: 'هشدار', color: 'warning', icon: 'la-exclamation-triangle'}
    default:
      return {label: 'ناموفق', color: 'danger', icon: 'la-times-circle'}
  }
}
